import { VideoUpload } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import {
  SponsoringInstitution,
  UserProfileResponse,
} from '@backrs_ui/grow-ui/src/redux/profile/profile';

import { Box, Divider, SxProps, Theme, Typography } from '@mui/material';

import { useAgentContext } from './OwnStudentSnapshot.agentContext';
import { PublicProfileSection } from '../atoms/PublicProfileSection';
import { SnapshotEnrollmentSection } from '../atoms/SnapshotEnrollmentSection';
import { useStudentDetails } from '../hooks/useStudentDetails';

const STRINGS = appStrings.modules.profile.public;

type OwnStudentSnapshotProps = Readonly<{
  profile: UserProfileResponse;
  sponsoringInstitutions: readonly SponsoringInstitution[];
}>;

export const OwnStudentSnapshot = ({
  profile,
  sponsoringInstitutions,
}: OwnStudentSnapshotProps) => {
  const { gradeLabel, major, allValues, profileValueOptions } = useStudentDetails(profile);

  const { city, state } = profile.profile.publicProfile;
  const location = [city, state].filter(Boolean).join(', ');
  const introVideoUrl = profile.profile.documentsPurposeVideo?.[0]?.uri;

  useAgentContext({ location, gradeLabel, major, sponsoringInstitutions });

  return (
    <Box id="own-student-snapshot" sx={containerSx}>
      <Typography variant="text-lg-semibold">{STRINGS.snapshot}</Typography>
      <Divider />

      <Box sx={detailsGridSx}>
        <SnapshotEnrollmentSection
          publicProfile={profile}
          profileValueOptions={profileValueOptions}
          findAllStringValues={allValues}
        />
        <PublicProfileSection
          title={STRINGS.location}
          variant={{ type: 'ICON_LIST', iconName: 'MarkerPin01', values: [location] }}
        />
        <PublicProfileSection
          title={STRINGS.yearGrade}
          variant={{ type: 'ICON_LIST', iconName: 'Clock', values: [gradeLabel ?? ''] }}
        />
        <PublicProfileSection
          title={STRINGS.sponsoringInstitution}
          variant={{
            type: 'CHIP',
            chipType: 'tag',
            borderColor: 'light',
            values: sponsoringInstitutions.map(({ name }) => name),
          }}
        />
        <PublicProfileSection
          title={STRINGS.major}
          variant={{ type: 'ICON_LIST', iconName: 'Certificate01', values: [major] }}
        />
      </Box>

      <PublicProfileSection
        title={STRINGS.interests}
        variant={{
          type: 'CHIP',
          chipType: 'pill',
          borderColor: 'dark',
          values: [
            ...allValues('hobbiesCreativeInterests'),
            ...allValues('hobbiesPassions'),
            ...allValues('hobbiesFavorites'),
          ],
          showMoreConfig: {
            minPerCategory: 3,
            categories: ['hobbiesCreativeInterests', 'hobbiesPassions', 'hobbiesFavorites'],
          },
        }}
      />
      <PublicProfileSection
        title={STRINGS.languages}
        variant={{
          type: 'ICON_LIST',
          iconName: 'Translate01',
          values: allValues('resourcesLanguages'),
        }}
      />
      <PublicProfileSection
        title={STRINGS.organizations}
        variant={{
          type: 'CHIP',
          chipType: 'tag',
          borderColor: 'light',
          values: allValues('resourcesClubs'),
        }}
      />

      {introVideoUrl && (
        <PublicProfileSection
          title={STRINGS.introVideo}
          variant={{
            type: 'CHILDREN',
            children: <VideoUpload mode="view" videoUrl={introVideoUrl} />,
          }}
        />
      )}
    </Box>
  );
};

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-3xl'],
  marginX: { xs: backrs.spacing['spacing-xl'], sm: backrs.spacing['spacing-4xl'] },
});

const detailsGridSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
  gap: backrs.spacing['spacing-3xl'],
});
